import { tokPx } from '@sunwebgroup/ui-components'
import { Button, DateField, TextField } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

const TextFieldContainer = styled.div`
  max-width: ${tokPx('primitives_area_size_480')};
  width: 100%;
`

const UserProfileForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${tokPx('primitives_space_16')};
  align-items: flex-start;
  width: 100%;
`

export function UserProfileEditForm({
  register,
  errors,
  onSubmit,
  handleSubmit,
  isSaving,
  intl,
}: {
  register: ReturnType<any>['register']
  errors: any
  onSubmit: (data: { firstName: string, lastName: string, birthDateUnformatted: string }) => void
  handleSubmit: ReturnType<any>['handleSubmit']
  isSaving: boolean
  intl: ReturnType<typeof useIntl>
}) {
  const today = new Date()
  const maxDate = today.toISOString().split('T')[0]
  const minDateObj = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())
  const minDate = minDateObj.toISOString().split('T')[0]

  return (
    <UserProfileForm onSubmit={handleSubmit(onSubmit)}>
      <TextFieldContainer>
        <TextField
          label={intl.formatMessage({ id: 'user_profile_first_name_label' })}
          helpText={!errors.firstName ? intl.formatMessage({ id: 'user_profile_first_name_help' }) : undefined}
          feedbackText={errors.firstName?.message}
          status={errors.firstName ? 'error' : undefined}
          {...register('firstName', {
            required: intl.formatMessage({ id: 'user_profile_first_name_required' }),
            minLength: { value: 2, message: intl.formatMessage({ id: 'user_profile_first_name_min_length' }) },
            maxLength: { value: 60, message: intl.formatMessage({ id: 'user_profile_first_name_max_length' }) },
            pattern: {
              value: /^[a-zA-Zèéêëēėęÿûüùúūîïíīįìôöòóœøōõàáâäæãåāßśšłžźżçćčñń\s-]+$/,
              message: intl.formatMessage({ id: 'user_profile_first_name_invalid_chars' }),
            },
          })}
        />
      </TextFieldContainer>
      <TextFieldContainer>
        <TextField
          label={intl.formatMessage({ id: 'user_profile_last_name_label' })}
          helpText={!errors.lastName ? intl.formatMessage({ id: 'user_profile_last_name_help' }) : undefined}
          feedbackText={errors.lastName?.message}
          status={errors.lastName ? 'error' : undefined}
          {...register('lastName', {
            required: intl.formatMessage({ id: 'user_profile_last_name_required' }),
            minLength: { value: 2, message: intl.formatMessage({ id: 'user_profile_last_name_min_length' }) },
            maxLength: { value: 60, message: intl.formatMessage({ id: 'user_profile_last_name_max_length' }) },
            pattern: {
              value: /^[a-zA-Zèéêëēėęÿûüùúūîïíīįìôöòóœøōõàáâäæãåāßśšłžźżçćčñń\s-]+$/,
              message: intl.formatMessage({ id: 'user_profile_last_name_invalid_chars' }),
            },
          })}
        />
      </TextFieldContainer>
      <TextFieldContainer>
        <DateField
          label={intl.formatMessage({ id: 'user_profile_birthday_label' })}
          minDate={minDate}
          maxDate={maxDate}
          status={errors.birthDateUnformatted ? 'error' : undefined}
          feedbackText={errors.birthDateUnformatted?.message}
          {...register('birthDateUnformatted', {
            required: intl.formatMessage({ id: 'user_profile_birthday_required' }),
            validate: (value: string) => {
              if (value < minDate) {
                return intl.formatMessage({ id: 'user_profile_birthday_too_early' })
              }
              if (value > maxDate) {
                return intl.formatMessage({ id: 'user_profile_birthday_too_late' })
              }
              return true
            },
          })}
        />
      </TextFieldContainer>
      <Button $type="primary" $size="M" type="submit" disabled={isSaving}>
        {intl.formatMessage({ id: 'user_profile_save' })}
      </Button>
    </UserProfileForm>
  )
}
